<template>
  <div :class="`swiper-holder w-full ltr ${isLoading && 'loading-slider'} slider m-auto relative`">
    <!-- shows when loading -->
    <div  v-if="isLoading">
      <div class="animate-pulse" role="status">
        <div class="max-h-[290px] sm:max-h-[331px] h-[12vh] md:h-[33vh] lg:h-[38vh] min-h-[150px] bg-gray-200 w-full dark:bg-gray-700"/>
      </div>
    </div>
    <!-- shows when loaded -->
    <swiper
        :autoplay="{ delay: 3000, disableOnInteraction: false }"
        :lazy="true"
        :loop="true"
        :grab-cursor="false" :modules="[Autoplay, Pagination, Navigation]" :pagination="{ clickable: true }"
            :space-between="50"
            :speed="500" class="rounded-xl overflow-hidden md:rounded-[2.375rem] md:overflow-visible" @slide-change="onActiveIndexChange" @after-init="onSwiperInit">
      <template v-if="!isLoading">
        <swiper-slide v-for="(slide, index) in slidersList" :key="index">
          <div :style="{ cursor: slide && slide.url ? 'pointer' : 'auto' }"
               @click="() => handleSliderClick(slide)">
            <!-- mobile image -->
            <MobileImage :imageName="slide.name" :imageURL="slide.image" :index="index"/>
            <!-- website image -->
            <DesktopImage :imageName="slide.name" :imageURL="slide.web_image" :index="index"/>
          </div>
        </swiper-slide>
      </template>
    </swiper>
  </div>
</template>

<script>
import {ref} from "vue";
import MobileImage from "./MobileImage.vue";
import DesktopImage from "./DesktopImage.vue";
import {Swiper, SwiperSlide} from "swiper/vue";
import {Autoplay, Navigation, Pagination} from "swiper/modules";
import { APIREQUESTS, MAINROLES } from "@/helpers";

import "swiper/css";

export default {
  components: {
    Swiper,
    SwiperSlide,
    MobileImage,
    DesktopImage
  },
  data() {
    return {
      Autoplay,
      Pagination,
      Navigation
    };
  },
  props: {
    slidersList: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const leftArrow = ref(null);
    const rightArrow = ref(null);
    return {
      leftArrow,
      rightArrow
    };
  },
  methods: {
    directToURL(url) {
      if (url) {
        window.open(url, '_self');
      }
    },
    handleSliderClick(slide) {
      // Track click if slider has an id
      if (slide && slide.id) {
        this.trackSliderClick(slide.id);
      }
      // Navigate to URL if available
      if (slide && slide.url) {
        this.directToURL(slide.url);
      }
    },
    trackSliderClick(sliderId) {
      // Get user role from localStorage or use guest
      const lsCopy = this.useLocalStorage({ type: 'get' });
      const userRole = lsCopy?.user?.role || 'guest';
      
      // Track click silently - don't show errors to user
      this.makeRequest({
        specificRole: userRole,
        owner: MAINROLES.user,
        method: APIREQUESTS.user.sliderClicks.method,
        url: APIREQUESTS.user.sliderClicks.url,
        formData: { slider_id: sliderId } ,
        includeUID: true,
      }).catch(err => {
        // Fail silently - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Slider click tracking failed:', err);
        }
      });
    },
    handleArrows() {
      const to_right = document.getElementById("go-right");
      const to_left = document.getElementById("go-left");
      if (!to_left?.value || !to_right?.value) {
        return;
      }
      to_right.style.display = "none";
      to_left.style.display = "none";
      if (
          window.innerWidth >= 700 &&
          window.innerWidth <= 1150 &&
          this.slidersList.length > 2
      ) {
        to_right.style.display = "inline-block";
        to_left.style.display = "inline-block";
      }
      if (window.innerWidth < 700 && this.slidersList.length > 1) {
        to_right.style.display = "inline-block";
        to_left.style.display = "inline-block";
      }
      if (window.innerWidth > 1150 && this.slidersList.length > 3) {
        to_right.style.display = "inline-block";
        to_left.style.display = "inline-block";
      }
    },
    onActiveIndexChange(event) {
      const {isEnd, isBeginning} = event;
      if (!this.rightArrow?.value || !this.leftArrow?.value) {
        return;
      }
      if (isBeginning) {
        this.rightArrow.value.classList?.add("disabled");
        this.leftArrow.value.classList?.remove("disabled");
        return;
      } else if (isEnd) {
        this.leftArrow.value.classList?.add("disabled");
        this.rightArrow.value.classList.remove("disabled");
        return;
      }
      this.rightArrow.value.classList.remove("disabled");
      this.leftArrow.value.classList.remove("disabled");
    },
    onSwiperInit() {
      if (!this.rightArrow?.value || !this.leftArrow?.value) {
        return;
      }
      this.rightArrow.value.classList.add("disabled");
    }
  }
};
</script>

<style scoped>
.slider.ltr {
  direction: ltr;
  text-align: left;
}

.swiper {
  position: relative;
}

.swiper-pagination {
  position: absolute;
  z-index: 3;
  bottom: 6% !important;
}

.slider h3::after {
  content: "";
  position: absolute;
  height: 6px;
  width: 54px;
  background-color: #ffffff;
  bottom: -15px;
  left: 0;
  border-radius: 20px;
}

.slider .swiper-pagination.swiper-pagination-bullets.swiper-pagination-horizontal {
  width: 100%;
  height: 54px;
  display: flex;
  justify-content: center;
  gap: 5px;
  align-items: flex-end;
}

.slider .swiper-pagination.swiper-pagination-bullets.swiper-pagination-horizontal span {
  width: 32px;
  height: 7px;
  background: #fff 0% 0% no-repeat padding-box;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  opacity: 1;
  cursor: pointer;
}

.slider .swiper-pagination.swiper-pagination-bullets.swiper-pagination-horizontal span.swiper-pagination-bullet.swiper-pagination-bullet-active {
  background: #0963ff 0% 0% no-repeat padding-box;
}

.loading-slider.slider .swiper-pagination.swiper-pagination-bullets.swiper-pagination-horizontal span.swiper-pagination-bullet.swiper-pagination-bullet-active {
  background: #bababb 0% 0% no-repeat padding-box;
}

@media (max-width: 670px) {
  .slider .swiper-pagination.swiper-pagination-bullets.swiper-pagination-horizontal span {
    width: 33px;
  }
}
</style>